import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { withTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Button, Grid, useMediaQuery } from "@mui/material";
import PropTypes from "prop-types";
import moment from "moment";

import Backdrop from "Components/Common/Backdrop";
import BreadCrumb from "Components/Common/BreadCrumb";
import DatePicker from "Components/Common/DatePicker";
import { dateFormatter } from "utils/dateFormatter";
import InputFieldText from "Components/Common/InputFieldText";
import BaseTable from "Components/Common/BaseTable";
import { billingSubscriptionTypes } from "common/enums";
import SelectField from "Components/Common/SelectField";

import { isAuthorizedUser } from "utils";

import { useLazyDownloadExcelQuery } from "slices/api";
import { useLazyGetOutstandingInvoiceQuery } from "apis/Payment";

const RECORDS_PER_PAGE = 10;

const columns = [
  { title: "Consumer Number", field: "consumerNumber" },
  { title: "Consumer Name", field: "consumerName" },
  { title: "Invoice No.", field: "invoiceNo" },
  { title: "Total Out Standing", field: "totalOutStanding" },
];

const OutstandingInvoice = ({ t }) => {
  const [downloadExcel, { isLoading: isExcelFileDownloading }] =
    useLazyDownloadExcelQuery();
  const resetPageRef = useRef();

  const [query, setQuery] = useState({
    year: moment(),
    consumerNumber: "",
    subscriptionType: "",
  });
  const [consumerNumber, setConsumerNumber] = useState("");
  const [subscriptionType, setSubscriptionType] = useState("");
  const [isResetPageRegistered, setIsResetPageRegistered] = useState(false);
  const [year, setYear] = useState(moment());

  const sm = useMediaQuery("(max-width: 599px)");
  const { roles } = useSelector((state) => state.Login);

  const [
    getOutstandingInvoiceData,
    { data: { data: outstandingInvoiceData } = {}, isLoading },
  ] = useLazyGetOutstandingInvoiceQuery();

  const handleResetPage = useCallback(resetPageRef.current, [
    isResetPageRegistered,
  ]);
  const updatedBillingSubscriptionOptions = useMemo(
    () =>
      billingSubscriptionTypes.filter(({ allowedRoles }) =>
        isAuthorizedUser(roles, allowedRoles)
      ),
    [billingSubscriptionTypes]
  );

  useEffect(() => {
    if (updatedBillingSubscriptionOptions.length && !subscriptionType) {
      setQuery((prev) => ({
        ...prev,
        subscriptionType: updatedBillingSubscriptionOptions[0].value,
      }));
      setSubscriptionType(updatedBillingSubscriptionOptions[0].value);
    }
  }, [updatedBillingSubscriptionOptions, subscriptionType]);

  useEffect(() => {
    if (query.subscriptionType) {
      getOutstandingInvoiceData({
        reportYear: query.year.format("YYYY"),
        consumerNumber: query.consumerNumber,
        subscriptionType: query.subscriptionType,
      });
    }
  }, [query]);

  return (
    <>
      <Backdrop open={isExcelFileDownloading || isLoading} />

      <BreadCrumb title={t("Outstanding Invoice")} pageTitle={t("Payment")} />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleResetPage();
          setQuery({
            year,
            consumerNumber,
            subscriptionType,
          });
        }}
      >
        <Grid container columnSpacing={2} className="mb-1">
          <Grid item lg={3} md={4} sm={6} xs={12}>
            <SelectField
              label={t("Subscription Type") + "*"}
              onChange={(_, { value }) => setSubscriptionType(value)}
              value={
                updatedBillingSubscriptionOptions.find(
                  ({ value }) => value === subscriptionType
                ) ?? ""
              }
              options={updatedBillingSubscriptionOptions}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <DatePicker
              slotProps={{
                textField: {
                  size: "small",
                },
              }}
              label={t("Select Year")}
              className="w-100"
              views={["year"]}
              format={dateFormatter(year)}
              value={year}
              disableFuture
              onChange={(date) => setYear(date)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <InputFieldText
              placeholder={t("Consumer Number")}
              value={consumerNumber}
              type="number"
              onChange={(e) => setConsumerNumber(e.target.value)}
              label={t("Consumer Number")}
            />
          </Grid>

          <Grid item>
            <Button
              variant="outlined"
              className="mb-2 me-2"
              type="submit"
              sx={{ maxHeight: "40px" }}
            >
              {t("Search")}
            </Button>
            <Button
              variant="outlined"
              className="mb-2 me-2"
              onClick={() => {
                handleResetPage();
                setConsumerNumber("");
                setQuery((prev) => ({
                  year: prev.year,
                  consumerNumber: "",
                  subscriptionType: updatedBillingSubscriptionOptions[0].value,
                }));
                setSubscriptionType(updatedBillingSubscriptionOptions[0].value);
              }}
              sx={{ maxHeight: "40px" }}
            >
              {t("Clear")}
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              className="mb-2 me-2"
              onClick={async () =>
                await downloadExcel(
                  {
                    endpoint:
                      "/report/outstanding-invoice-details-of-consumer-excel",
                    sheetName: "Outstanding Invoice",
                    query: {
                      reportYear: query.year.format("YYYY"),
                      subscriptionType,
                    },
                  },
                  true
                )
              }
              sx={{ maxHeight: "40px" }}
            >
              {t("Download")}
            </Button>
          </Grid>
        </Grid>
      </form>
      <BaseTable
        columns={columns}
        data={outstandingInvoiceData}
        registerResetPage={(cb) => {
          resetPageRef.current = cb;
          setIsResetPageRegistered((prev) => !prev);
        }}
        pageSize={RECORDS_PER_PAGE}
        maxHeight={sm ? "calc(100vh - 300px)" : "calc(100vh - 260px)"}
      />
    </>
  );
};

OutstandingInvoice.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(OutstandingInvoice);
